/**
 * Coach LLM — Prompt Builder
 *
 * Pure transform: takes a list of observations + scope context + profile
 * runtime, produces { system, user } strings ready for the Anthropic API.
 *
 * Design philosophy:
 *
 *   1. The LLM is a NARRATOR, not an analyst. The hard work (mining trades,
 *      computing percentiles, detecting patterns) is already done by the
 *      analyzers. The LLM's job is to weave the findings into a single
 *      cohesive paragraph that reads like a coach speaking.
 *
 *   2. Every claim in the paragraph MUST trace back to an observation we
 *      passed in. No invention, no extrapolation. The system prompt
 *      constrains this strictly so we never get hallucinated stats.
 *
 *   3. The output is short — 3-5 sentences. We're replacing the templated
 *      synthesis sentence ("The strongest signal is X..."), not generating
 *      a long-form report.
 *
 *   4. Temporal context is woven in naturally when present ("Coach has
 *      been flagging this for 4 weeks") instead of being bolted on at the
 *      end like the templated narrator does.
 *
 * Cache key generation: deterministic from the observation signatures +
 * scope so repeated calls with identical context hit the cache instead of
 * re-burning tokens.
 */

const SYNTHESIS_KEY = 'synthesis';

// Backwards-compat: SYSTEM_PROMPT used to be a static export. The Mentor
// preset (`voicePersonalities.MENTOR`) is now the canonical home of this
// text, but tests + any future caller that imports SYSTEM_PROMPT directly
// keeps working via this re-export.
import { getVoicePrompt, DEFAULT_VOICE } from './voicePersonalities.js';
const SYSTEM_PROMPT = getVoicePrompt(DEFAULT_VOICE);

/**
 * Format ONE observation into a compact line the LLM can reason about.
 * Drops fields the LLM doesn't need (ids, evidence schemas) — keeps
 * title, detail, action, severity, and temporal state.
 */
const formatObservation = (obs, idx) => {
  const lines = [];
  const sevLabel = obs.severity?.toUpperCase() || 'INFO';
  lines.push(`[${idx + 1}] (${sevLabel}) ${obs.title}`);
  if (obs.detail) lines.push(`    ${obs.detail}`);
  if (obs.action) lines.push(`    ACTION: ${obs.action}`);
  // Temporal context — only include when meaningful (persistent runs >= 2
  // or returning state). The templated badge logic agrees: 1st-run isn't
  // worth flagging.
  const tc = obs.temporalContext;
  if (tc) {
    if (tc.state === 'persistent' && tc.runs >= 2) {
      lines.push(`    HISTORY: This pattern has fired in ${tc.runs} consecutive snapshots${tc.firstSeenAt ? ` since ${formatDateOnly(tc.firstSeenAt)}` : ''}.`);
    } else if (tc.state === 'returning') {
      lines.push(`    HISTORY: This pattern was previously resolved${tc.lastResolvedAt ? ` on ${formatDateOnly(tc.lastResolvedAt)}` : ''} and has come back this run.`);
    }
  }
  return lines.join('\n');
};

const formatDateOnly = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const SCOPE_LABEL = {
  setup:    (id) => `setup "${id}"`,
  atm:      (id) => `ATM strategy "${id}"`,
  day:      (id) => `the trading day ${id || 'today'}`,
  session:  () => 'this session',
  progress: () => 'the last 30 days',
};

/**
 * Build the user message for the API call.
 *
 * @param {Object} args
 * @param {Array}  args.observations  Pre-ranked, optionally temporally enriched
 * @param {string} args.scope         'setup' | 'atm' | 'day' | …
 * @param {string} args.scopeId       The entity name or date
 * @param {number} [args.tradeCount]  Sample size (just included for grounding)
 * @returns {string}
 */
export const buildUserPrompt = ({ observations = [], scope = 'global', scopeId = '', tradeCount = null }) => {
  // Drop synthesis observations — they're meta and would create a recursive
  // self-reference if we passed them back to the LLM that's about to
  // re-synthesize.
  const usable = observations.filter(o => o && o.analyzer !== SYNTHESIS_KEY);
  const sevOrder = { critical: 0, warning: 1, success: 2, info: 3 };
  const ranked = [...usable].sort((a, b) => {
    const sevDiff = (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9);
    if (sevDiff !== 0) return sevDiff;
    return (b.priority || 0) - (a.priority || 0);
  });

  const scopeNoun = (SCOPE_LABEL[scope] || ((id) => id || scope))(scopeId);
  const sampleSize = tradeCount != null
    ? `\nSample size: ${tradeCount} trade${tradeCount === 1 ? '' : 's'}.`
    : '';

  const obsBlock = ranked.length === 0
    ? '(No observations of meaningful severity.)'
    : ranked.slice(0, 8).map(formatObservation).join('\n\n');

  return `Write a single 3-5 sentence coaching paragraph about ${scopeNoun}.${sampleSize}

OBSERVATIONS (ranked by severity then priority):

${obsBlock}

Remember: every number AND every direction word you cite must come from the observations above — preserve the observation's exact directional vocabulary ("loosening," "tightening," "widening," "narrowing," "increased," "decreased"). Do not substitute a generic phrase like "tighter trailing" when the data describes the opposite direction. Lead with the highest-impact signal, but frame moves as choices, not commands — the trader iterates, the coach surfaces options. If you close with a next step, pull it from an ACTION field and offer it as something to consider, A/B-test, or investigate (not as an order to "roll back" or "stop doing X"). When the data points to a hypothesis the trader could falsify with the next handful of trades, name it.`;
};

/**
 * Build the system prompt for a coaching call. Optional `voice` arg
 * picks a personality preset (mentor/analyst/drill/friend); falls back
 * to the default voice when omitted or unknown.
 */
export const buildSystemPrompt = (voice) => {
  return voice ? getVoicePrompt(voice) : SYSTEM_PROMPT;
};

/**
 * Deterministic cache key for an observation set in a given scope. Two
 * scope runs that produce the same signature set + same scope/id +
 * SAME VOICE should hit the cache, not re-burn LLM tokens.
 *
 * Includes voice in the key because switching personality changes the
 * prose dramatically — returning cached Mentor prose when the user
 * just switched to Drill Sergeant would be a surprise. Different voice
 * = different cache slot.
 *
 * Sort + join observation ids so order doesn't matter — the analyzer
 * firing order can vary across runs but the cache should hit.
 */
export const buildCacheKey = ({ scope = '', scopeId = '', observations = [], voice = '' }) => {
  const ids = observations
    .filter(o => o && o.analyzer !== SYNTHESIS_KEY)
    .map(o => o.id || `${o.analyzer || 'anon'}:${o.scopeId || ''}:${o.title || ''}`)
    .sort();
  // voice is appended to the key so different personalities don't collide.
  // Empty voice (legacy callers) keeps the original key shape — backwards-compat.
  return voice
    ? `${scope}|${scopeId}|${voice}|${ids.join(',')}`
    : `${scope}|${scopeId}|${ids.join(',')}`;
};

// Exports for tests
export { formatObservation, SYSTEM_PROMPT, SCOPE_LABEL };
